import type { Question, SurveyContent, SurveySubmission } from './contract'
import { answerFormatValid, serializeDateAnswer } from './question-precision'
import { isTextQuestion, rendersOtherInput, resolveSelectionLimit, selectionCap, submitField, textFormatValid, violatesSelectionLimit } from './question-type'

// The widget's view phases. 'empty' = nothing to show (serve no-fill OR a load failure);
// the host collapses (display:none) and renders nothing — never an error card. Load
// failures (204 / 4xx / 5xx / network) fold into 'empty', not a dedicated error phase.
export type Phase = 'loading' | 'active' | 'ended' | 'results' | 'empty'

// One submit-response entry, mirroring the POST /responses body (type-only — no Zod
// pulled into the bundle).
export type SubmitResponse = SurveySubmission['response'][number]

// A flow step — one per answerable question, including demographics: the backend types those
// as ordinary bank questions and the reader answers them inline like any other.
export interface Step { q: Question }

// 是否可承接「晚到的 mpid」並重發載入：只在使用者尚未開始作答時可以——
// loading（還沒拿到資料）、empty（no-fill／載入失敗已收合，換真身分後可能就投得中，見
// load() 頂部）、或 active 但完全沒作答。已送出 / 結果頁 / 已結束都不換身分。
export function canAdoptMpid(
  phase: Phase,
  optionAnswers: Record<string, string[]>,
  textAnswers: Record<string, string>,
): boolean {
  if (phase === 'loading' || phase === 'empty')
    return true
  if (phase !== 'active')
    return false
  const noOptions = Object.keys(optionAnswers).length === 0
  const noText = Object.values(textAnswers).every(v => v.trim().length === 0)
  return noOptions && noText
}

// Ordered answerable steps. Bank/custom questions share the same survey order.
export function buildSteps(survey: Pick<SurveyContent, 'questions'>): Step[] {
  return [...survey.questions].sort((a, b) => a.seq - b.seq).map(q => ({ q }))
}

// 「非空但格式不符」→ 給 prefill 過濾存下來的舊答案用。空值不算錯（空/必填交由 answerGap 處理）。
export function textAnswerFormatInvalid(type: Question['type'], value: string): boolean {
  const text = value.trim()
  if (text.length === 0)
    return false
  return !textFormatValid(type, text)
}

// 這一題還缺什麼才算答完。undefined = 沒缺口。呼叫端據此決定要不要放行（見
// incompleteQuestionIds）以及要對使用者說什麼（render/question.ts 的提示文案以此為 key，
// 所以這裡新增一種缺口，那張表會編不過）。反過來說，新規則若沿用既有的缺口名，型別檢查
// 就幫不上忙 —— 那時要自己去看那句話還說不說得中。
export type AnswerGap
  = | 'required' // 必填但整題沒作答
    | 'text-format' // 有填但格式不符（哪些題型有格式規則見 question-type.ts 的 format 欄）
    | 'other-text-missing' // 選了「其他」卻沒寫自由填答
    | 'selection-limit' // 複選題有勾但選取數量不符 selection_limit（至少／至多／恰好）

export function answerGap(
  step: Step,
  optionAnswers: Record<string, string[]>,
  textAnswers: Record<string, string>,
): AnswerGap | undefined {
  const q = step.q
  const qKey = String(q.id)
  // 文字題：空值能否略過看 is_required；只要非空就一律得通過該題型的格式檢查，選填也不例外
  // —— buildSubmitResponses 對格式一律照送，不在這裡攔就會把亂填的日期／email 送到後端。
  if (isTextQuestion(q)) {
    const text = (textAnswers[qKey] ?? '').trim()
    if (text.length === 0)
      return q.is_required ? 'required' : undefined
    // 走 answerFormatValid 而不是題型自己的規則：只問年份的 date 題，畫面上放的是四位數，
    // 拿完整日期的規則去驗它會把每一筆都判成格式錯誤（見 question-precision.ts）。
    return answerFormatValid(q, text) ? undefined : 'text-format'
  }
  const picked = optionAnswers[qKey] ?? []
  // 整題沒勾：必填才有缺口，選填直接略過（空的作答不套選取數量限制）。
  if (picked.length === 0)
    return q.is_required ? 'required' : undefined
  // 選了「其他」就得把自由填答寫完，否則這題等於只送出一個沒有內容的選項。選填題不擋這個：
  // 選填題本來就能整題不答，為了自由填答去擋一題可略過的題目只會擋路（沒填字時也不會
  // 送出空的 text）。
  if (q.is_required && pickedOtherSeq(q, picked) !== undefined && (textAnswers[qKey] ?? '').trim().length === 0)
    return 'other-text-missing'
  // 有勾就得符合選取數量限制，選填也不例外 —— 送出時不符的答案一樣會被送上去。
  if (violatesSelectionLimit(resolveSelectionLimit(q), new Set(picked).size))
    return 'selection-limit'
  return undefined
}

// 被選中的「其他」選項 seq；沒選到、這題沒有 is_other 選項、或這個題型不會渲染自由填答輸入框
// （見 question-type.ts 的 rendersOtherInput，例如 dropdown）一律回 undefined —— 兩個呼叫點
// （answerGap 的必填判斷、buildSubmitResponses 的 text 欄位）因此自動一致：題型不認輸入框，
// 就不會要求讀者填一個畫面上不存在的欄位，也不會送出畫面上填不出來的文字。
// 契約假設一題最多一個 is_other 選項，所以回單一 seq 而非陣列
// （見 docs/survey-api-contract-gaps.md P0；若後端最終允許多個，這裡與送出格式都要改）。
function pickedOtherSeq(q: Question, picked: string[]): number | undefined {
  if (!rendersOtherInput(q))
    return undefined
  return (q.options ?? []).find(o => o.is_other && picked.includes(String(o.seq)))?.seq
}

// Fan out the answers into the POST body shape. Unanswered option questions and blank
// free-text answers are skipped.
export function buildSubmitResponses(
  survey: Pick<SurveyContent, 'questions'>,
  optionAnswers: Record<string, string[]>,
  textAnswers: Record<string, string>,
): SubmitResponse[] {
  const out: SubmitResponse[] = []
  for (const q of survey.questions) {
    const qKey = String(q.id)
    // 自由填答題：答案落在哪個欄位由行為表決定（見 question-type.ts 的 field），但宣告的
    // question_type 一律是題目自己的型別 —— 後端拿 question_id 去驗才算數，前端把型別改寫成
    // 別的只會讓送上去的東西與題目對不起來。
    const field = submitField(q.type)
    if (field !== undefined) {
      const text = (textAnswers[qKey] ?? '').trim()
      if (text.length === 0)
        continue
      // date 欄位一律送完整日期：只問年份的題目在畫面上是四位數，serializeDateAnswer 把它
      // 補成該年的第一天；其餘 date 題原樣通過。
      //
      // 這裡跟著上面那條「格式一律照送」走，不自己重驗：擋格式的是 answerGap（推進與送出
      // 都經過它）。所以序列化拿到什麼就補什麼 —— 一個沒過閘門的 `19` 會變成 `19-01-01`。
      // 那不是這裡該補的洞，是閘門被繞過了才會發生的事。
      out.push(field === 'date'
        ? { question_id: q.id, question_type: q.type, date: serializeDateAnswer(q, text) }
        : { question_id: q.id, question_type: q.type, text })
      continue
    }
    const picked = optionAnswers[qKey] ?? []
    if (picked.length === 0)
      continue
    // Defensive invariant: even if optionAnswers somehow carries more picks than the
    // question's selection cap (stale state, a bypassed UI guard), the payload sent to the
    // backend must never exceed it. The toggle handler is the primary guard; this is the
    // last line of defense right before the POST body is built. Only at_most and exactly
    // have a cap — at_least has no upper bound, so it is never truncated.
    const cap = selectionCap(resolveSelectionLimit(q))
    const capped = cap !== null ? picked.slice(0, cap) : picked
    // 「其他」的自由填答沿用既有的 text 欄位（choice 題型原本不送 text，所以那個位置是空的）。
    // 只有在「其他」真的被選中時才送 —— 使用者先選「其他」打了字、又改選一般選項時，
    // textAnswers 會留著那串字，照送等於送出一段沒有選項承載的孤兒文字。
    const otherText = pickedOtherSeq(q, capped) !== undefined ? (textAnswers[qKey] ?? '').trim() : ''
    out.push({
      question_id: q.id,
      question_type: q.type,
      selected: capped.map(Number),
      ...(otherText.length > 0 ? { text: otherText } : {}),
    })
  }
  return out
}

// 整份問卷是否至少答了一題。判準刻意對齊送出 body —— 「有作答」＝ buildSubmitResponses
// 至少組得出一筆，所以空白字元、換問卷後殘留的答案、格式不符而不會被送出的東西都不算數。
// 全部題目都是選填時，這是唯一擋得住「一題不答直接送出一筆空回應」的地方。
export function hasAnyAnswer(
  survey: Pick<SurveyContent, 'questions'>,
  optionAnswers: Record<string, string[]>,
  textAnswers: Record<string, string>,
): boolean {
  return buildSubmitResponses(survey, optionAnswers, textAnswers).length > 0
}

// 推進前的頁面驗證：回傳當前頁還有缺口（見 answerGap）的題目 id，維持 steps 原順序，
// 呼叫端據此標紅並捲到第一個。空陣列 = 這一頁可以前進，下一頁／完成鈕可以按。
export function incompleteQuestionIds(
  steps: Step[],
  optionAnswers: Record<string, string[]>,
  textAnswers: Record<string, string>,
): string[] {
  return steps
    .filter(s => answerGap(s, optionAnswers, textAnswers) !== undefined)
    .map(s => String(s.q.id))
}
